import {
  BadgeIcon,
  BuildingIcon,
  HashIcon,
  LogOutIcon,
  MapPinIcon,
  PhoneIcon,
} from "lucide-react";
import client from "../api/client";
import type { EmployeeInfo } from "../api/QuikTripClient";
import { separateName, toTitleCase } from "../lib/util";

interface Props {
  employeeInfo: EmployeeInfo;
  onLogout: () => void;
}

export function ProfilePage({ employeeInfo, onLogout }: Props) {
  const { firstName, middleInit, lastName } = separateName(employeeInfo.name);
  const displayName = toTitleCase(
    `${firstName}${middleInit ? ` ${middleInit}` : ""} ${lastName}`,
  );
  const initials = `${firstName[0] ?? ""}${lastName[0] ?? ""}`;

  function handleLogout() {
    if (localStorage.getItem("demoMode") === "true") {
      localStorage.removeItem("demoMode");
    }
    client.logout();
    onLogout();
  }

  return (
    <div className="bg-base-100 flex min-h-full flex-col">
      {/* Hero */}
      <div
        className="bg-primary relative flex flex-col items-center gap-3 px-6 pt-10 pb-16"
        style={{
          background: "linear-gradient(160deg, #c8102e 0%, #8b0020 100%)",
        }}
      >
        <div className="flex h-24 w-24 items-center justify-center rounded-full border-2 border-white/30 bg-white/15 shadow-lg">
          <span className="text-3xl font-black tracking-tight text-white">
            {initials}
          </span>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold tracking-wide text-white">
            {displayName}
          </div>
          <div className="mt-0.5 text-sm text-white/70">
            {employeeInfo.job_Type_Desc}
          </div>
        </div>
        <div className="flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1">
          <HashIcon size={12} className="text-white/60" />
          <span className="font-mono text-xs text-white/80">
            {employeeInfo.emp_ID}
          </span>
        </div>
      </div>

      {/* Card pulled up over hero */}
      <div className="-mt-6 flex flex-1 flex-col gap-3 px-4 pb-6">
        {/* Info card */}
        <div className="card bg-base-200 shadow-lg">
          <div className="card-body divide-base-300 divide-y p-0">
            <InfoRow
              icon={<BuildingIcon size={16} />}
              label="Store"
              value={`#${employeeInfo.base_Store}`}
            />
            <InfoRow
              icon={<MapPinIcon size={16} />}
              label="Address"
              value={employeeInfo.storeAddress}
            />
            <InfoRow
              icon={<PhoneIcon size={16} />}
              label="Phone"
              value={employeeInfo.storePhone}
            />
            <InfoRow
              icon={<BadgeIcon size={16} />}
              label="Division"
              value={employeeInfo.division}
            />
          </div>
        </div>

        {/* Permissions */}
        {employeeInfo.permissions.length > 0 && (
          <div className="card bg-base-200 shadow-lg">
            <div className="card-body gap-2 p-4">
              <div className="text-base-content/50 text-xs font-semibold tracking-widest uppercase">
                Permissions
              </div>
              <div className="flex flex-wrap gap-1.5">
                {employeeInfo.permissions.map((p) => (
                  <span
                    key={p}
                    className="badge badge-sm badge-ghost border-base-300 border text-xs"
                  >
                    {p}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Sign out */}
        <button
          type="button"
          onClick={handleLogout}
          className="btn btn-ghost text-error border-error/30 mt-2 w-full gap-2 border"
        >
          <LogOutIcon size={16} />
          Sign Out
        </button>
      </div>
    </div>
  );
}

function InfoRow({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-start gap-3 px-4 py-3">
      <div className="text-primary mt-0.5 shrink-0">{icon}</div>
      <div className="min-w-0 flex-1">
        <div className="text-base-content/50 mb-0.5 text-xs tracking-widest uppercase">
          {label}
        </div>
        <div className="text-sm leading-snug font-medium">{value || "—"}</div>
      </div>
    </div>
  );
}
